// RenewalOptions.jsx
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MdCheckCircle,
  MdClose,
  MdChevronRight,
  MdInfoOutline,
  MdExpandMore,
  MdExpandLess,
} from "react-icons/md";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import reactLogo from "../assets/policy-logo.png";
import "../styles/ren.css";
import CONFIG from "../utils/config";

// Coverage Panel Component (Modified to be always visible)
const CoverageSelectionPanel = ({
  selectedPlanId,
  plans,
  selectedOptions,
  basePrice,
  onOptionToggle,
  formatPrice,
}) => {
  const currentPlan = plans.find((plan) => plan.id === selectedPlanId);
  const [expandedGroups, setExpandedGroups] = useState({});

  if (!currentPlan) return null;

  const mandatoryItems = currentPlan.coverages.filter(
    (item) => item.applicableStatus === "M"
  );
  const optionalItems = currentPlan.coverages.filter(
    (item) => item.applicableStatus === "O"
  );

  // Group optional items by cover group
  const groupedOptions = {};
  optionalItems.forEach((item) => {
    const groupKey = item.irpCoverGroup || "other";
    if (!groupedOptions[groupKey]) {
      groupedOptions[groupKey] = [];
    }
    groupedOptions[groupKey].push(item);
  });

  const optionsPrice = selectedOptions.reduce((total, code) => {
    const item = optionalItems.find((opt) => opt.coverageCode === code);
    return total + (item ? item.premium : 0);
  }, 0);

  const totalPrice = basePrice + optionsPrice;

  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  return (
    <div className="coverage-panel">
      <div className="panel-header">
        <h3 className="panel-title">Coverage Details</h3>
      </div>

      <div className="panel-content">
        {/* <div className="coverage-section">
          <h5 className="coverage-section-title">Mandatory Coverages</h5>
          <ul className="options-list">
            {mandatoryItems.map((item, index) => (
              <li key={`mandatory-${index}`} className="option-item option-mandatory">
                <div className="option-checkbox">
                  <input type="checkbox" checked readOnly />
                </div>
                <div className="option-details">
                  <h6 className="option-name">{item.coverageDescShort}</h6>
                  <small className="option-price">{formatPrice(item.premium)}</small>
                </div>
              </li>
            ))}
          </ul>
        </div> */}

        {Object.keys(groupedOptions).length > 0 && (
          <div className="coverage-section">
            <h5 className="coverage-section-title">Optional Coverages</h5>

            {Object.entries(groupedOptions).map(([groupKey, items]) => {
              const groupName = items[0].irpCoverGroup
                ? `Car Replacement Cover`
                : "Other Covers";
              const isExpanded = expandedGroups[groupKey];

              return (
                <div key={groupKey} className="option-group">
                  <div
                    className="option-group-header"
                    onClick={() => toggleGroup(groupKey)}
                  >
                    <h6 className="option-group-title">{groupName}</h6>
                    {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                  </div>

                  {isExpanded && (
                    <ul className="options-list">
                      {items.map((item, index) => (
                        <li
                          key={`optional-${index}`}
                          className={`option-item ${
                            selectedOptions.includes(item.coverageCode)
                              ? "option-selected"
                              : ""
                          }`}
                          onClick={() => onOptionToggle(item.coverageCode)}
                        >
                          <div className="option-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedOptions.includes(
                                item.coverageCode
                              )}
                              readOnly
                            />
                          </div>
                          <div className="option-details">
                            <h6 className="option-name">
                              {item.coverageDescShort}
                            </h6>
                            <small className="option-price">
                              {formatPrice(item.premium)}
                            </small>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="panel-footer">
        <div className="price-summary">
          <div className="price-row">
            <span className="price-label">Base Premium:</span>
            <span className="price-value">{formatPrice(basePrice)}</span>
          </div>
          <div className="price-row">
            <span className="price-label">Optional Coverages:</span>
            <span className="price-value">{formatPrice(optionsPrice)}</span>
          </div>
          <hr className="price-divider" />
          <div className="price-row total-row">
            <span className="price-label">Total Premium:</span>
            <span className="price-value">{formatPrice(totalPrice)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Current Plan Card Component
const CurrentPlanCard = ({
  plan,
  isActive,
  totalPrice,
  onSelectPlan,
  formatPrice,
  policyNumber,
  proposalNumber,
}) => {
  const mandatoryItems = plan.coverages.filter(
    (item) => item.applicableStatus === "M"
  );
  const [showCoverageTooltip, setShowCoverageTooltip] = useState(false);

  return (
    <div
      className={`current-plan-card ${isActive ? "plan-active" : ""}`}
      onClick={() => onSelectPlan(plan.name, plan.id)}
    >
      <div className="plan-card-body">
        <h5 className="plan-title">
          {plan.name}
          <span className="current-badge">Current Plan</span>
          <span
            className="info-icon-wrapper"
            onMouseEnter={() => setShowCoverageTooltip(true)}
            onMouseLeave={() => setShowCoverageTooltip(false)}
          >
            <MdInfoOutline className="info-icon" />
            {showCoverageTooltip && (
              <div className="coverage-tooltip">
                <h6 className="tooltip-title">Mandatory Coverages:</h6>
                <ul className="tooltip-list">
                  {mandatoryItems.map((item, idx) => (
                    <li key={`info-${idx}`}>{item.coverageDescShort}</li>
                  ))}
                </ul>
              </div>
            )}
          </span>
        </h5>

        <div className="plan-price-section">
          <h4 className="plan-price">
            {formatPrice(totalPrice)}
            <small className="price-period">/year</small>
          </h4>
        </div>
      </div>
    </div>
  );
};

// Plan Card Component
const PlanCard = ({
  plan,
  isActive,
  selectedOptions,
  onSelectPlan,
  formatPrice,
}) => {
  const optionalItems = plan.coverages.filter(
    (item) => item.applicableStatus === "O"
  );
  const mandatoryItems = plan.coverages.filter(
    (item) => item.applicableStatus === "M"
  );

  const optionsPrice = selectedOptions.reduce((total, code) => {
    const item = optionalItems.find((opt) => opt.coverageCode === code);
    return total + (item ? item.premium : 0);
  }, 0);

  return (
    <div
      className={`plan-card ${isActive ? "plan-active" : ""}`}
      onClick={() => onSelectPlan(plan.name, plan.id)}
    >
      <div className="plan-card-content">
        <div className="plan-header">
          <h5 className="plan-name">{plan.name}</h5>
        </div>

        <div className="price-breakdown">
          <div className="price-row">
            <span>Base Premium:</span>
            <span>{formatPrice(plan.calculatedBasePremium)}</span>
          </div>
          {isActive && optionalItems.length > 0 && (
            <div className="price-row">
              <span>Optional Coverages:</span>
              <span>{formatPrice(optionsPrice)}</span>
            </div>
          )}
          <hr className="price-divider" />
          <div className="price-row total-row">
            <span>Total Premium:</span>
            <span>
              {formatPrice(
                isActive
                  ? plan.calculatedBasePremium + optionsPrice
                  : plan.calculatedBasePremium
              )}
            </span>
          </div>
        </div>

        <h6 className="includes-title">Coverage Includes:</h6>
        <ul className="included-items">
          {mandatoryItems.map((item, idx) => (
            <li key={`item-${idx}`}>
              <MdCheckCircle className="included-icon" />{" "}
              {item.coverageDescShort}
            </li>
          ))}
          {isActive &&
            selectedOptions.map((code, idx) => {
              const option = optionalItems.find(
                (opt) => opt.coverageCode === code
              );
              return option ? (
                <li key={`optional-${idx}`}>
                  <MdCheckCircle className="included-icon optional-icon" />{" "}
                  {option.coverageDescShort}
                </li>
              ) : null;
            })}
        </ul>
      </div>
      <div className="plan-footer">
        <button
          className={`select-plan-btn ${
            isActive ? "btn-selected" : "btn-unselected"
          }`}
        >
          {isActive ? "Selected Plan" : "Select Plan"}
        </button>
      </div>
    </div>
  );
};

const PricingSummaryModal = ({
  show,
  onClose,
  onContinue,
  data,
  formatPrice,
  isLoading,
}) => {
  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h4 className="modal-title">Pricing Summary</h4>
          <button onClick={onClose} className="modal-close-btn">
            <MdClose />
          </button>
        </div>
        <div className="modal-body">
          {isLoading ? (
            <div className="loading-indicator">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div className="pricing-summary-details">
              <div className="price-row">
                <span className="price-label">Premium:</span>
                <span className="price-value">{formatPrice(data.premium)}</span>
              </div>

              {data.discount > 0 && (
                <div className="price-row">
                  <span className="price-label">Discount:</span>
                  <span className="price-value">
                    {formatPrice(data.discount)}
                  </span>
                </div>
              )}

              {data.vat > 0 && (
                <div className="price-row">
                  <span className="price-label">VAT:</span>
                  <span className="price-value">{formatPrice(data.vat)}</span>
                </div>
              )}

              {data.premLoading > 0 && (
                <div className="price-row">
                  <span className="price-label">Loading:</span>
                  <span className="price-value">
                    {formatPrice(data.premLoading)}
                  </span>
                </div>
              )}

              <hr className="price-divider" />
              <div className="price-row total-row">
                <span className="price-label">Total Premium:</span>
                <span className="price-value">
                  {formatPrice(data.totalPremium)}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onContinue}
            disabled={isLoading}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const RenewalOptions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, passedActivationDate, passedExpirationDate } =
    location.state || {};
// near top of RenewalOptions.jsx (with other useStates)
const [showPageLoader, setShowPageLoader] = useState(false);

  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPlanName, setSelectedPlanName] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [basePrice, setBasePrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [renPolicyNumber, setRenPolicyNumber] = useState("");
  const [conversionFailed, setConversionFailed] = useState(false);
  const [activationDate, setActivationDate] = useState(
    passedActivationDate || null
  );
  const [expirationDate, setExpirationDate] = useState(
    passedExpirationDate || null
  );
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [pricingSummary, setPricingSummary] = useState(null);
//console.log("wdggd",passedActivationDate)
  useEffect(() => {
    fetchPlanData();
  }, [id]);

  const fetchPlanData = async () => {
    setIsLoading(true);
    try {
      if (!id) throw new Error("No ID found in navigation state.");

      // Fetch policy data
      const policyResponse = await fetch(`${CONFIG.BASE}/insurance/getsgs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: Number(id) }),
      });

      if (!policyResponse.ok) throw new Error("Failed to fetch policy data");
      const policyData = await policyResponse.json();
      setPolicyData(policyData);

      if (!passedActivationDate && policyData.policyStartDate) {
        setActivationDate(policyData.policyStartDate);
      }
      if (!passedExpirationDate && policyData.policyExpiryDate) {
        setExpirationDate(policyData.policyExpiryDate);
      }

      // Fetch plan data
      const planResponse = await fetch(`${CONFIG.BASE}/plan-prem/get`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });

      if (!planResponse.ok) {
        const errorText = await planResponse.text();
        console.error("❌ /plan-prem/get failed. Error body:", errorText);
        throw new Error(`Server error: ${planResponse.status}`);
      }

      const planData = await planResponse.json();
      const planMap = {};

      for (const item of planData) {
        const planId = parseInt(item.irpCoveragePlanCode, 10);
        if (isNaN(planId)) continue;

        if (!planMap[planId]) {
          planMap[planId] = {
            id: planId,
            name: item.irpPlanName || `Plan ${planId}`,
            description: item.planDescription || "No description available.",
            limit: item.planLimit1Description || "Not specified",
            coverages: [],
            calculatedBasePremium: 0,
          };
        }

        const coverage = {
          coverageCode: item.irpCoverageCode?.toString(),
          coverageName: item.irpCoverageName,
          coverageDescShort: item.coverageDescShort || item.irpCoverageName,
          premium: item.irpCoveragePremium ?? 0,
          applicableStatus: item.irpCoverageApplicableYn || "M",
          irpCoverGroup: item.irpCoverGroup || null,
        };

        if (coverage.applicableStatus === "M") {
          planMap[planId].calculatedBasePremium += coverage.premium;
        }

        planMap[planId].coverages.push(coverage);
      }

      const availablePlans = Object.values(planMap);
      setPlans(availablePlans);

      const defaultPlan =
        availablePlans.find((p) => p.id === 0) || availablePlans[0];
      if (defaultPlan) {
        setSelectedPlanName(defaultPlan.name);
        setSelectedPlanId(defaultPlan.id);
        setBasePrice(defaultPlan.calculatedBasePremium);
        setTotalPrice(defaultPlan.calculatedBasePremium);

        // Populate selectedOptions with initially selected (mandatory) items
        const initialOptions = defaultPlan.coverages
          .filter((c) => c.applicableStatus === "M")
          .map((c) => c.coverageCode);
        setSelectedOptions(initialOptions);
      }

      setIsLoading(false);
    } catch (error) {
      toast.error("Failed to load plans. " + error.message);
      setIsLoading(false);
    }
  };

  const handlePlanSelection = (planName, planId) => {
    if (planId === selectedPlanId) return;

    setSelectedPlanName(planName);
    setSelectedPlanId(planId);
    const plan = plans.find((p) => p.id === planId);
    if (plan) {
      const mandatoryOptions = plan.coverages
        .filter((c) => c.applicableStatus === "M")
        .map((c) => c.coverageCode);
      setBasePrice(plan.calculatedBasePremium);
      setTotalPrice(plan.calculatedBasePremium);
      setSelectedOptions(mandatoryOptions);
    }
  };

  const handleOptionToggle = (optionCode) => {
    const currentPlan = plans.find((p) => p.id === selectedPlanId);
    if (!currentPlan) return [];

    const clickedOption = currentPlan.coverages.find(
      (c) => c.coverageCode === optionCode && c.applicableStatus === "O"
    );
    if (!clickedOption) return [];

    setSelectedOptions((prev) => {
      let updatedOptions = [...prev];

      if (clickedOption.irpCoverGroup) {
        const alreadySelected = updatedOptions.filter((code) => {
          const opt = currentPlan.coverages.find(
            (c) => c.coverageCode === code
          );
          return opt && opt.irpCoverGroup === clickedOption.irpCoverGroup;
        });

        if (alreadySelected.length > 0 && !prev.includes(optionCode)) {
          toast.info(
            "You can only select one option from this group. The previous selection was removed."
          );
        }

        updatedOptions = updatedOptions.filter((code) => {
          const opt = currentPlan.coverages.find(
            (c) => c.coverageCode === code
          );
          return !(opt && opt.irpCoverGroup === clickedOption.irpCoverGroup);
        });

        if (!prev.includes(optionCode)) {
          updatedOptions.push(optionCode);
        }
      } else {
        if (prev.includes(optionCode)) {
          updatedOptions = updatedOptions.filter((code) => code !== optionCode);
        } else {
          updatedOptions.push(optionCode);
        }
      }

      const mandatoryCovers = currentPlan.coverages
        .filter((c) => c.applicableStatus === "M")
        .map((c) => c.coverageCode);
      const allSelectedCovers = [
        ...new Set([...mandatoryCovers, ...updatedOptions]),
      ];

      let optionsPrice = 0;
      allSelectedCovers.forEach((code) => {
        const option = currentPlan.coverages.find(
          (c) => c.coverageCode === code
        );
        if (option) optionsPrice += option.premium;
      });

      setTotalPrice(optionsPrice);
      return updatedOptions;
    });
  };

  const proceedToPayment = async () => {
    if (!selectedPlanName || !policyData || selectedPlanId === null) {
      toast.error("Please select a plan first.");
      return;
    }

    try {
      setLoading(true);

      const selectPlanRequestBody = {
        mobileNumber: policyData.mobileNumber,
        selectedPlan: selectedPlanId.toString(),
        premiumAmount: totalPrice,
        policyNumber: policyData.policyNumber || undefined,
        activationDate: activationDate,
        expirationDate: expirationDate,
      };

      const selectPlanResponse = await fetch(
        `${CONFIG.BASE}/insurance/select-plan`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(selectPlanRequestBody),
        }
      );

      if (!selectPlanResponse.ok) throw new Error("Plan selection failed.");
      toast.success("Plan submitted successfully.");

      const currentPlan = plans.find((p) => p.id === selectedPlanId);
      const mandatoryCovers =
        currentPlan?.coverages
          .filter((c) => c.applicableStatus === "M")
          .map((c) => c.coverageCode) || [];

      const optionalCovers = selectedOptions.map((sel) => {
        const opt = currentPlan?.coverages.find(
          (c) => c.coverageName === sel || c.coverageCode === sel
        );
        return opt ? opt.coverageCode : sel;
      });

      const updateSelectionRequestBody = {
        irpIrgSgsId: id,
        selectedPlanCode: selectedPlanId.toString(),
        selectedCovers: [...new Set([...mandatoryCovers, ...optionalCovers])],
      };

      const updateSelectionResponse = await fetch(
        `${CONFIG.BASE}/plan-prem/update-selection`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updateSelectionRequestBody),
        }
      );

      if (!updateSelectionResponse.ok)
        throw new Error("Failed to update plan selection.");
      toast.success("Plan and coverages updated successfully.");

      const calculateNcdVatRequestBody = {
        sgsId: Number(id),
        proposalNo: policyData.renProposalNumber,
        planCode: selectedPlanId,
      };

      const ncdVatResponse = await fetch(
        `${CONFIG.BASE}/insurance/calculate-ncd-vat`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(calculateNcdVatRequestBody),
        }
      );

      if (!ncdVatResponse.ok)
        throw new Error("Failed to calculate NCD and VAT.");
      const ncdVatData = await ncdVatResponse.json();
      //console.log("loading deatils",ncdVatData);

     // const ncdDiscount = parseFloat(ncdVatData.ncd) || 0;
     const ncdDiscount = 0;
      const vatAmount = parseFloat(ncdVatData.vat) || 0;
      const premLoading = parseFloat(ncdVatData.premLoad) || 0;
     //console.log("loading seaparete",premLoading);

      const premiumBeforeDiscounts = totalPrice;
      const finalPremium =
        premiumBeforeDiscounts - ncdDiscount + premLoading + vatAmount;

      setPricingSummary({
        premium: premiumBeforeDiscounts,
        discount: ncdDiscount,
        vat: vatAmount,
        totalPremium: finalPremium,
        premLoading: premLoading,
      });
      setShowSummaryModal(true);
    } catch (error) {
      console.error("Error during pricing calculation:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Add inside RenewalOptions component (state)
  const [showNoCoverageModal, setShowNoCoverageModal] = useState(false);
  const [noCoverageModalShown, setNoCoverageModalShown] = useState(false);
  const [showNoCoverageModal2, setShowNoCoverageModal2] = useState(false);
  // Modified proceedToPayment handler
  const handleProceedToPayment = () => {
    const currentPlan = plans.find((p) => p.id === selectedPlanId);
    const optionalItems =
      currentPlan?.coverages.filter((c) => c.applicableStatus === "O") || [];
    const selectedOptional = selectedOptions.filter((code) =>
      optionalItems.some((opt) => opt.coverageCode === code)
    );

    if (selectedOptional.length === 0 && !noCoverageModalShown) {
      setShowNoCoverageModal(true);
      setNoCoverageModalShown(true);
      return; // stop flow until modal closed
    }

    proceedToPayment(); // continue with original logic
  };

  // const handleModalContinue = async () => {
  //   setShowSummaryModal(false);
  //   setLoading(true);


  //   if (!pricingSummary || pricingSummary.premium <= 0) {
  //     toast.error("Premium value is invalid. Cannot proceed to payment.");
  //     return;
  //   }
  //   try {
  //     const convertRes = await fetch(
  //       `${CONFIG.BASE}/insurance/convert-ren-quote`,
  //       {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify({
  //           irgSgsId: Number(id),
  //           renQuoteNo: policyData.renProposalNumber,
  //           selectPlan: selectedPlanId,
  //         }),
  //       }
  //     );
  //     const convertData = await convertRes.json();
  //     setRenPolicyNumber(convertData.renPolicyNumber || "N/A");
  //     toast.success("Renewal converted successfully!");

  //     const paymentPayload = {
  //       amount: pricingSummary.totalPremium.toFixed(2),
  //       firstName: policyData.insuredName,
  //       lastName: policyData.insuredName,
  //       email: policyData.email,
  //       transactionId: `${policyData.renProposalNumber}-${id}`,
  //       returnUrl: `${CONFIG.APPROVAL}`,
  //     };

  //     const paymentResponse = await fetch(
  //       `${CONFIG.PAYMENT}/payment/generate-signature`,
  //       {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify(paymentPayload),
  //       }
  //     );

  //     if (!paymentResponse.ok) throw new Error("Payment processing failed.");
  //     const paymentData = await paymentResponse.json();

  //     if (paymentData.status !== "SUCCESS" || !paymentData.payUrl) {
  //       throw new Error("Invalid payment response.");
  //     }

  //     window.location.href = paymentData.payUrl;
  //   } catch (err) {
  //     console.error("❌ Conversion or payment failed:", err);
  //     toast.error(
  //       `Sorry, renewal failed! ${err.message || "Please try again later."}`
  //     );
  //     setConversionFailed(true);
  //   } finally {
  //     setLoading(false);
  //   }
  // };


  const handleModalContinue = async () => {
    setShowSummaryModal(false);
    setLoading(true);
    setShowPageLoader(true);
  
    if (!pricingSummary || pricingSummary.premium <= 0) {
      toast.error("Premium value is invalid. Cannot proceed to payment.");
      return;
    }
  
    try {
      const convertRes = await fetch(
        `${CONFIG.BASE}/insurance/convert-ren-quote`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            irgSgsId: Number(id),
            renQuoteNo: policyData.renProposalNumber,
            selectPlan: selectedPlanId,
          }),
        }
      );
  
      const convertData = await convertRes.json();
  
      // ✅ Allow only errorCode 1 or 2 to proceed
      // if (convertData.errorCode !== "1" && convertData.errorCode !== "2") {
      //   toast.error(
      //     `Conversion failed! ${convertData.errorDesc}`
      //   );
      //   setShowErrorModal(true);
     

      //   setLoading(false);
      //   return;
      // }

      if (convertData.errorCode !== "1" && convertData.errorCode !== "2") {
        const message = ` ${convertData.errorDesc || "Unknown error."}`;
      
     //   toast.error(message);          
        setErrorMessage(message);     
        setShowErrorModal(true);      
        setLoading(false);
        setShowPageLoader(false);
        return;
      }
      
  
      setRenPolicyNumber(convertData.renPolicyNumber || "N/A");
     // toast.success("Renewal converted successfully!");
  
      // const paymentPayload = {
      //   amount: pricingSummary.totalPremium.toFixed(2),
      //   firstName: policyData.insuredName,
      //   lastName: policyData.insuredName,
      //   email: policyData.email,
      //   transactionId: `${policyData.renProposalNumber}-${id}`,
      //   returnUrl: `${CONFIG.APPROVAL}`,
      // };

      const fullName = (policyData.insuredName || "").trim();

// Optional: split if first & last name are combined
const [firstName = "", ...lastParts] = fullName.split(/\s+/);
const lastName = lastParts.join(" ");

const paymentPayload = {
  amount: pricingSummary.totalPremium.toFixed(2),
  firstName: firstName.trim(),
  lastName: lastName.trim(),
  email: policyData.email?.trim(),
  transactionId: `${policyData.renProposalNumber}-${id}`,
  returnUrl: CONFIG.APPROVAL,
};

  
      const paymentResponse = await fetch(
        `${CONFIG.PAYMENT}/payment/generate-signature`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(paymentPayload),
        }
      );
      console.log("payment resposne log",paymentResponse);
      console.log("payment payload log",paymentPayload);
      if (!paymentResponse.ok) throw new Error("Payment processing failed.");
      const paymentData = await paymentResponse.json();

      if (paymentData.status !== "SUCCESS" || !paymentData.payUrl) {
        throw new Error("Invalid payment response.");
      }
  
      window.location.href = paymentData.payUrl;
    } catch (err) {
      console.error("Conversion or payment failed:", err);
      toast.error(
        `Sorry, renewal failed! ${err.message || "Please try again later."}`
      );
      setConversionFailed(true);
      setLoading(false);
      setShowPageLoader(false);
    }
  };
  
  const formatPrice = (amount) =>
    new Intl.NumberFormat("en-QA", {
      style: "currency",
      currency: "QAR",
    }).format(amount);

  const currentPlan = plans.find((plan) => plan.id === 0);
  const availablePlans = plans.filter((plan) => plan.id !== 0);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  return (
 
    
    
    <div className="renewal-container">
       {showPageLoader && (
      <div className="page-loader-overlay">
        <div className="page-loader-spinner"></div>
        <p>Processing your payment, please wait...</p>
      </div>
    )}
{showNoCoverageModal && (
  <div className="modal-overlay">
    <div className="modal-content">
      <div className="modal-body text-center">
        <p className="modal-message option-group-title">
        Please check & avail other optional coverages, for better protection
        </p>
      </div>
      <div className="modal-footer text-center" style={{ display: "flex", justifyContent: "center" }}>
        <button
          className="continue-btn"
          onClick={() => setShowNoCoverageModal(false)}
        >
          OK
        </button>
      </div>
    </div>
  </div>
)}

<div className="">
    {showErrorModal && (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-body text-center">
            <p className="modal-message option-group-title">
              {errorMessage}
            </p>
          </div>
          <div
            className="modal-footer text-center"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <button
              className="continue-btn"
              onClick={() => setShowErrorModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    )}
    </div>


      <div className="renewal-content">
        <div className="renewal-header">
          <img src={reactLogo} alt="Insurance Logo" className="header-logo" />
          <h2 className="header-title">Choose Your Insurance Plan</h2>
        </div>

        {isLoading ? (
          <div className="loading-indicator">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <div className="plan-selection-layout">
            <div className="plan-cards-container">
              {policyData && (
                <div className="policy-info-section mb-4">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="card-title text-primary mb-3">
                        Policy Information
                      </h5>
                      <div className="row text-center">
                        <div className="col-md-6 mb-2">
                          <div className="p-3 bg-light rounded">
                            <strong>Existing Policy:</strong>
                            <div>{policyData.policyNumber || "N/A"}</div>
                          </div>
                        </div>
                        <div className="col-md-6 mb-2">
                          <div className="p-3 bg-light rounded">
                            <strong>Proposal No:</strong>
                            <div>{policyData.renProposalNumber || "N/A"}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {currentPlan && (
                <div className="current-plan-section mb-4">
                  <CurrentPlanCard
                    plan={currentPlan}
                    isActive={currentPlan.id === selectedPlanId}
                    totalPrice={currentPlan.calculatedBasePremium}
                    onSelectPlan={handlePlanSelection}
                    formatPrice={formatPrice}
                    policyNumber={policyData?.policyNumber}
                    proposalNumber={policyData?.renProposalNumber}
                  />
                </div>
              )}

              {availablePlans.length > 0 && (
                <div className="available-plans-section">
                  <div className="plans-grid">
                    {availablePlans.map((plan) => (
                      <div key={plan.id} className="plan-card-wrapper">
                        <PlanCard
                          plan={plan}
                          isActive={plan.id === selectedPlanId}
                          selectedOptions={selectedOptions}
                          onSelectPlan={handlePlanSelection}
                          formatPrice={formatPrice}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <CoverageSelectionPanel
              selectedPlanId={selectedPlanId}
              plans={plans}
              selectedOptions={selectedOptions}
              basePrice={basePrice}
              onOptionToggle={handleOptionToggle}
              formatPrice={formatPrice}
            />
          </div>
        )}
      </div>

      <div className="action-bar">
        <div className="action-bar-content">
          <div className="total-price-display">
            <h5 className="total-label">Total Premium:</h5>
            <h4 className="total-value">{formatPrice(totalPrice)}</h4>
          </div>
          <div className="action-buttons">
            <button
              className="cancel-btn"
              onClick={() => navigate(-1, { state: { id } })}
            >
              Back
            </button>
            {/* <button className="continue-btn" onClick={proceedToPayment}>Continue to Payment</button> */}
            <button className="continue-btn" onClick={handleProceedToPayment}>
              Continue to Payment
            </button>
          </div>
        </div>
      </div>

      <PricingSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        onContinue={handleModalContinue}
        data={pricingSummary}
        formatPrice={formatPrice}
        isLoading={loading}
      />
    </div>

    
  );
};

export default RenewalOptions;
